import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ChevronRight } from 'lucide-react'
import { apiRequest } from '@/lib/api'
import { useAuthStore } from '@/stores/auth-store'
import { ActivityRow } from '@/components/activity/ActivityRow'
import type { ActivityRecord } from '@/components/activity/ActivityRow'

export function RecentActivity({ bidId }: { bidId: string }) {
  const [items, setItems] = useState<ActivityRecord[]>([])
  const [loading, setLoading] = useState(true)
  const currentUserId = useAuthStore((s) => s.user?.id ?? '')

  useEffect(() => {
    let cancelled = false

    async function load() {
      try {
        const { items: records } = await apiRequest<{ items: ActivityRecord[]; total: number }>(
          `/bids/${bidId}/activity?limit=5`
        )
        if (!cancelled) setItems(records)
      } catch {
        // Silently fail
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    void load()
    return () => { cancelled = true }
  }, [bidId])

  if (loading) return null

  return (
    <section>
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-sm font-medium">Recent Activity</h2>
        <Link
          to={`/app/bids/${bidId}/activity`}
          className="inline-flex items-center gap-0.5 text-xs text-[hsl(var(--link))] hover:underline"
        >
          View all
          <ChevronRight className="h-3.5 w-3.5 shrink-0" aria-hidden />
        </Link>
      </div>

      {items.length === 0 ? (
        <p className="text-sm text-muted-foreground">No activity yet.</p>
      ) : (
        <div className="rounded-xl border bg-card shadow-sm p-4">
          <div className="divide-y divide-muted -my-2.5">
            {items.map((item) => (
              <ActivityRow
                key={item.id}
                record={item}
                currentUserId={currentUserId}
                bidId={bidId}
              />
            ))}
          </div>
        </div>
      )}
    </section>
  )
}
